import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Mousewheel, FreeMode } from "swiper/modules";
import styles from "./Component/CSS/SimilarSection.module.css";
import { useNewSimilarAudios } from "../../hooks/useAudiosHooks";
import Loading from "../../common/HousGlass Loader/Loading";
import AudiosUI from "../../UI/Audios/AudiosUI";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";

const AudioSeriesSlider = ({ audioData }) => {
  const navigate = useNavigate();
  const {
    data: similarAudiosData,
    isLoading,
    error,
  } = useNewSimilarAudios(audioData?.id);

  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);

  useEffect(() => {
    // This is to ensure navigation refs are properly set after component mounts
    if (navigationPrevRef.current && navigationNextRef.current) {
      navigationPrevRef.current.classList.add(styles.swiperButtonPrev);
      navigationNextRef.current.classList.add(styles.swiperButtonNext);
    }
  }, []);

  const handleItemClick = (slug) => {
    navigate(`/audio-play/${slug}`);
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorMessage}>Failed to load similar audios.</div>
    );
  }

  // If no similar audios or data not available
  if (
    !similarAudiosData ||
    !similarAudiosData.data ||
    similarAudiosData.data.length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h2 className={styles.title}>Similar Audios</h2>
        <div className={styles.navigationControls}>
          <div
            ref={navigationPrevRef}
            className={`${styles.navButton} ${styles.prevButton}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </div>
          <div
            ref={navigationNextRef}
            className={`${styles.navButton} ${styles.nextButton}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        </div>
      </div>

      <Swiper
        modules={[Navigation, Mousewheel, FreeMode]}
        freeMode={{
          enabled: true,
          momentum: true,
          momentumRatio: 0.8,
          momentumVelocityRatio: 0.8,
          minimumVelocity: 0.02,
        }}
        spaceBetween={10}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 1.5,
          releaseOnEdges: true,
        }}
        speed={600}
        resistance={false}
        resistanceRatio={0}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = navigationPrevRef.current;
          swiper.params.navigation.nextEl = navigationNextRef.current;
        }}
        breakpoints={{
          320: {
            slidesPerView: 2.4,
            spaceBetween: 8,
          },
          480: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          768: {
            slidesPerView: 4,
            spaceBetween: 15,
          },
          1024: {
            slidesPerView: 6,
            spaceBetween: 20,
          },
          1200: {
            slidesPerView: 7,
            spaceBetween: 20,
          },
        }}
        className={styles.swiperContainer}
      >
        {similarAudiosData.data.map((item) => (
          <SwiperSlide key={item.id}>
            <AudiosUI
              title={item.title}
              image={item.computer_image_url || item.mobile_image_url}
              onClick={() => handleItemClick(item.slug)}
              imageFit="cover"
              square
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default AudioSeriesSlider;
